import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TagManager from "react-gtm-module";
import { AdminArea, ClimbingArea } from "../../types";
import fetchAreaDetail from "../../api/areaDetail";
import fetchAdminAreas from "../../api/adminAreas";
import fetchFeaturedArea from "../../api/featuredArea";
import { getRecentlyViewedAreas } from "../../utils/areaHistory";
import { areaLink } from "../../utils/climbingAreaUtils";
import { forecastDayConditions, parseNumber, rateForecastDay } from "../../utils/conditions";
import { dayLabel, verdictLine } from "../../utils/areaForecast";
import { useFavorites } from "../../hooks/useFavorites";
import { AdminAreaLink } from "../common";
import { AreaRow, AreaRowSkeleton } from "../areaRow";
import { ConditionsMeter } from "../area/conditionsScore";
import { RatingButton } from "../ratingButton";
import { RatingsHelpLink } from "../ratingsExplainer";
import { SectionCard } from "../area/sectionCard";
import { IconClock, IconMountain, IconStar } from "../area/icons";
import { WeatherIcon } from "../weatherIcon";

// Most areas to fetch forecasts for in one list
const MAX_FAVORITES = 12;
const MAX_RECENT = 4;

/// Fetch areas with forecasts, in the given order, skipping any that fail
function useAreasWithForecasts(areaIds: string[]) {
    const [areas, setAreas] = useState<ClimbingArea[]>([]);
    const [loading, setLoading] = useState(areaIds.length > 0);
    const key = areaIds.join(",");

    useEffect(() => {
        let cancelled = false;
        const ids = key ? key.split(",") : [];
        if (ids.length === 0) {
            setAreas([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        Promise.all(ids.map((id) => fetchAreaDetail(id).catch(() => null))).then((results) => {
            if (!cancelled) {
                setAreas(results.filter((a): a is ClimbingArea => a !== null));
                setLoading(false);
            }
        });
        return () => { cancelled = true; };
    }, [key]);

    return { areas, loading };
}

function RowList({ count, loading, children }: { count: number; loading: boolean; children: ReactNode }) {
    return (
        <div className="home-rows">
            {loading ? [...Array(Math.max(count, 1))].map((_, i) => <AreaRowSkeleton key={i} />) : children}
        </div>
    );
}

export function YourAreas() {
    const favorites = useFavorites();
    const ids = favorites.slice(0, MAX_FAVORITES).map((f) => f.areaId);
    const { areas, loading } = useAreasWithForecasts(ids);

    return (
        <SectionCard icon={<IconStar />} title="Your areas">
            {favorites.length === 0 ? (
                <p className="cw-card__message">
                    Tap <strong>Save</strong> on any area’s forecast to keep it here.
                </p>
            ) : (
                <RowList count={ids.length} loading={loading}>
                    {areas.map((area) => <AreaRow key={area.areaId} area={area} />)}
                </RowList>
            )}
        </SectionCard>
    );
}

export function RecentAreas() {
    const favorites = useFavorites();
    const favoriteIds = new Set(favorites.map((f) => f.areaId));
    // Skip areas already shown under Your areas
    const ids = getRecentlyViewedAreas(MAX_RECENT + favorites.length)
        .filter((item) => !favoriteIds.has(item.areaId))
        .slice(0, MAX_RECENT)
        .map((item) => item.areaId);
    const { areas, loading } = useAreasWithForecasts(ids);

    if (ids.length === 0) {
        return null;
    }

    const handleAreaClick = (areaName: string) => {
        // Track click event in Google Analytics via GTM
        TagManager.dataLayer({
            dataLayer: {
                event: 'recently_viewed_click',
                area_name: areaName,
                navigation_source: 'recently_viewed'
            }
        });
    };

    return (
        <SectionCard icon={<IconClock />} title="Recently viewed">
            <RowList count={ids.length} loading={loading}>
                {areas.map((area) => (
                    <AreaRow key={area.areaId} area={area} onClick={() => handleAreaClick(area.name)} />
                ))}
            </RowList>
        </SectionCard>
    );
}

/// "Where you should be climbing now": the featured area on a dark card
export function FeaturedArea() {
    const [area, setArea] = useState<ClimbingArea | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        fetchFeaturedArea().then(setArea, () => setFailed(true));
    }, []);

    if (failed) {
        return null;
    }

    const days = area?.forecast?.daily?.data?.slice(0, 3) ?? [];
    const tz = area?.forecast?.timezone ?? "UTC";
    const now = Date.now() / 1000;
    const today = area?.forecast && days[0] ? rateForecastDay(area.forecast, 0) : null;

    return (
        <section className="home-featured" aria-labelledby="home-featured-title">
            {!area ? (
                <div className="home-featured__info" aria-hidden="true">
                    <p className="home-featured__eyebrow" id="home-featured-title">Where you should be climbing now</p>
                    <span className="cw-skeleton cw-skeleton--short" />
                    <span className="cw-skeleton" />
                </div>
            ) : (
                <>
                    <div className="home-featured__info">
                        <p className="home-featured__eyebrow" id="home-featured-title">Where you should be climbing now</p>
                        <h2 className="home-featured__name">
                            <Link to={areaLink(area)}>{area.name}</Link>
                        </h2>
                        <p className="home-featured__state">
                            <AdminAreaLink code={area.adminArea}>{area.adminAreaName}</AdminAreaLink>
                        </p>
                        {today && (
                            <div className="home-featured__verdict">
                                <RatingButton conditions={forecastDayConditions(area.forecast!, 0)} period="day" context={`${area.name} · Today`} onDark>
                                    <ConditionsMeter rating={today} suffix="today" onDark />
                                </RatingButton>
                                <p>{verdictLine(today)}</p>
                            </div>
                        )}
                        <Link to={areaLink(area)} className="home-featured__link">
                            See the full forecast
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6" /></svg>
                        </Link>
                    </div>
                    {days.length > 0 && (
                        <ul className="home-featured__days">
                            {days.map((dp, i) => {
                                const high = parseNumber(dp.temperatureHigh);
                                const low = parseNumber(dp.temperatureLow);
                                return (
                                    <li key={dp.time} className="home-featured__day">
                                        <span className="home-featured__dow">{dayLabel(dp.time, tz, now, true)}</span>
                                        <WeatherIcon code={dp.icon} size={36} onDark />
                                        <span className="home-featured__temps">
                                            {high !== undefined ? `${Math.round(high)}°` : "–"}
                                            <span className="home-featured__low">{low !== undefined ? ` ${Math.round(low)}°` : ""}</span>
                                        </span>
                                        <RatingButton
                                            conditions={forecastDayConditions(area.forecast!, i)}
                                            period="day"
                                            context={`${area.name} · ${dayLabel(dp.time, tz, now)}`}
                                            onDark
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </>
            )}
        </section>
    );
}

export function PopularAreas() {
    const [groups, setGroups] = useState<AdminArea[] | null>(null);

    useEffect(() => {
        // Front page states with their popular areas and 2-day forecasts
        fetchAdminAreas(true, true, true, 2).then(
            (adminAreas) => setGroups(adminAreas.filter((a) => a.areas && a.areas.length > 0)),
            (error) => {
                console.warn('Could not load popular areas with forecasts:', error);
                setGroups([]);
            }
        );
    }, []);

    if (groups !== null && groups.length === 0) {
        return null;
    }

    return (
        <section className="home-popular" aria-labelledby="home-popular-title">
            <h2 className="home-section-title" id="home-popular-title"><IconMountain />Popular areas</h2>
            <p className="home-rating-key">
                Tap or hover over a climb rating to see what’s behind it. <RatingsHelpLink />
            </p>
            <div className="home-popular__grid">
                {groups === null
                    ? [...Array(4)].map((_, i) => (
                        <div className="cw-card" key={i}>
                            <span className="cw-skeleton cw-skeleton--short" style={{ marginTop: 0 }} />
                            <RowList count={3} loading>{null}</RowList>
                        </div>
                    ))
                    : groups.map((group) => (
                        <SectionCard
                            key={group.code}
                            icon={null}
                            title={group.name}
                            aside={group.areaCount ? <span>{group.areaCount} areas</span> : undefined}
                        >
                            <div className="home-rows">
                                {group.areas!.map((area) => <AreaRow key={area.areaId} area={area} showState={false} />)}
                            </div>
                            <AdminAreaLink code={group.code} className="home-popular__all">
                                All {group.name} areas
                            </AdminAreaLink>
                        </SectionCard>
                    ))}
            </div>
        </section>
    );
}
